import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Subscription, BehaviorSubject } from 'rxjs';
import { 
  NotificationService, 
  Notification, 
  EnrichedNotification,
} from 'src/app/services/notification.service';

import { NotificationChannel } from '../../shared/constants/notification.constants'; 
import { trigger, transition, style, animate } from '@angular/animations';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { catchError, finalize, delay } from 'rxjs/operators';
import { of } from 'rxjs';
import { NotificationActionType } from '../constants/notification.constants';

type NotificationFilter = 'all' | 'unread';

@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss'],
  animations: [
    trigger('dropdownAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-20px)' }),
        animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ opacity: 0, transform: 'translateY(-20px)' }))
      ])
    ])
  ]
})
export class NotificationsComponent implements OnInit, OnDestroy {
  notifications: EnrichedNotification[] = [];
  unreadCount = 0;
  isNotificationsOpen = false;
  isLoading = false;
  selectedFilter: 'all' | 'unread' = 'all';

  private currentFilter = new BehaviorSubject<NotificationFilter>('all');
  filteredNotifications$ = new BehaviorSubject<EnrichedNotification[]>([]);

  private subscriptions: Subscription[] = [];

  constructor(
    public notificationService: NotificationService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.initializeSubscriptions();
    this.loadNotifications();
  }

  private initializeSubscriptions(): void {
    this.subscriptions.push(
      this.notificationService.getUnreadCount().subscribe(count => {
        this.unreadCount = count;
      })
    );

    this.subscriptions.push(
      this.notificationService.notificationsOpen$.subscribe(isOpen => {
        this.isNotificationsOpen = isOpen;
      })
    );

    this.subscriptions.push(
      this.currentFilter.subscribe(() => {
        this.updateFilteredNotifications();
      })
    );
  }

  private updateFilteredNotifications(): void {
    if (this.notifications.length > 0) {
      const filtered = this.notifications.filter(notification => 
        this.currentFilter.value === 'all' || !notification.read_at
      );
      this.filteredNotifications$.next(filtered);
    }
  }

  onFilterChange(filter: NotificationFilter): void {
    this.selectedFilter = filter;
    this.currentFilter.next(filter);
  }

  private loadNotifications(): void {
    this.isLoading = true;
    this.subscriptions.push(
      this.notificationService
        .getNotifications()
        .pipe(
          delay(0),
          catchError(error => {
            console.error('Error loading notifications:', error);
            return of([]);
          }),
          finalize(() => {
            this.isLoading = false;
          })
        )
        .subscribe(notifications => {
          if (notifications.length > 0) {
            this.notifications = notifications.map(n => {
              const enrichedNotification = this.processNotification(n);
              return enrichedNotification;
            });
            this.updateFilteredNotifications();
          }
          this.isLoading = false;
        })
    );
  }

  private processNotification(notification: Notification): EnrichedNotification {
    try {
      const enriched = NotificationService.getEnricher(notification.type)(notification);
      return {
        ...notification,
        enrichedMetadata: enriched
      };
    } catch (error) {
      console.error('Error processing notification:', error);
      return {
        ...notification,
        enrichedMetadata: { serviceInfo: undefined, action: undefined }
      };
    }
  }

  public formatPrice(price: number | undefined): string {
    if (!price) return '0.00';
    return price.toFixed(2);
  }

  getNotificationTime(notification: Notification): string {
    const date = new Date(notification.created_at);
    return date.toLocaleString('en-US', { 
      hour: 'numeric',
      minute: '2-digit',
      hour12: true 
    });
  }

  handleNotificationClick(notification: EnrichedNotification): void {
    if (!notification.read_at) {
      this.notificationService.markAsRead(notification.id).subscribe();
    }
  }

  handleActionClick(event: { event: Event; notification: EnrichedNotification }): void {
    event.event.stopPropagation();
    const action = event.notification.enrichedMetadata.action;
    
    if (!action) return;

    if (!event.notification.read_at) {
      this.notificationService.markAsRead(event.notification.id).subscribe();
    }

    const route = this.notificationService.getActionRoute(action);
    if (route.length > 0) {
      this.router.navigate(route);
      this.isNotificationsOpen = false;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const targetElement = event.target as HTMLElement;
    if (!targetElement.closest('.notification-container')) {
      this.isNotificationsOpen = false;
    }
  }

  toggleNotifications(): void {
    this.isNotificationsOpen = !this.isNotificationsOpen;
    this.notificationService.notificationsOpen.next(this.isNotificationsOpen);
  }

  getActionType(type: any): NotificationActionType {
    return type as NotificationActionType;
  }

  handleMarkAllAsRead(): void {
    if (this.unreadCount > 0) {
      this.notificationService.markAllAsRead().subscribe(() => {
        this.loadNotifications();
      });
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
} 